import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import axios from "axios";
import { Encrypt } from "../../utils/encryption";
import { handleMedia } from "../../config/api";
import {
  updateBizDetailsPatch,
  updateHubDetailsPatch,
  findItemsUsingGalleryImageBasenames,
  unlinkGalleryImagesFromItems,
} from "../Hub/StoreUtils";
import {
  setHubImageKey,
  setGalleryManifestForPath,
  clearGalleryManifestForPath,
} from "../../store/gallerySlice";
import {
  App as AntApp,
  Card,
  Button,
  Row,
  Col,
  Typography,
  Upload,
  Space,
  Checkbox,
  Tooltip,
  Progress,
} from "antd";
import {
  UploadOutlined,
  ReloadOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  StarFilled,
} from "@ant-design/icons";
import { isHeicFile } from "../../utils/heicConversion";
import { putPresignedObject } from "../../utils/putPresignedObject";
import {
  mediaPath,
  cdnUrlForStorageKey,
  basenameFromStorageKey,
  defaultItemGalleryTagsFromStoreType,
  cdnSafeMediaPathSegment,
  ITEMS_SHARED_GALLERY_ITEM_ID,
} from "../../config/mediaPaths";
import {
  fetchManifestForPath,
  invalidateManifestFetchCacheForPath,
  preferNewerOrRicherManifest,
  putManifestWithPresignedUrl,
  requestManifestPutPresignedUrl,
} from "../../utils/mediaManifest";
import { operationTypeForGalleryUpload } from "../../config/mediaOperationTypes";
import "./GallerySegmentPanel.css";

const { Text } = Typography;

const VALID_TYPES = ["image/jpg", "image/jpeg", "image/png", "image/webp"];
const IMAGE_UPLOAD_ACCEPT = "image/jpeg,image/png,image/webp";

const EMPTY_MANIFEST = { version: 1, images: [] };

/** @typedef {'hub' | 'biz' | 'items'} GallerySegment */

/**
 * Hub / Biz / Items gallery: manifest-backed listing, upload via media-handle (no UserAndHub galleryImageKeys).
 */
const GallerySegmentPanel = ({
  token,
  hubId,
  bizId,
  segment,
  /** Biz store type string for default product/service tags (items segment) */
  storeType,
  compact = false,
  /** Biz row version (required to save BizImageKey). */
  bizVersion,
  /** Full storage key from DB for display badge / biz “display image”. */
  bizImageKeyFromDb = "",
  hubImageKeyFromDb = "",
  onBizBrandingSaved,
  onHubImageSaved,
  onItemsGalleryUploadComplete,
  /**
   * Private Biz manifest encryption (passkey in memory only). Omit for hub segment or public Biz.
   * @type {{ bizPrivate?: boolean, passkey?: string, storageSalt?: string, storageIv?: string } | null | undefined}
   */
  manifestCrypto: _manifestCrypto = null,
  bizPrivate: _bizPrivate = false,
  imageLimit = 0,
}) => {
  const { message, modal } = AntApp.useApp();
  const dispatch = useDispatch();
  const store = useStore();

  const putManifestCrypto = null;
  const privateManifestLocked = false;
  const manifestPutOptions = { bizPrivate: false };

  const defaultTags = useMemo(
    () => defaultItemGalleryTagsFromStoreType(storeType),
    [storeType]
  );
  const [tagProduct, setTagProduct] = useState(() => defaultTags.includes("product"));
  const [tagService, setTagService] = useState(() => defaultTags.includes("service"));

  useEffect(() => {
    setTagProduct(defaultTags.includes("product"));
    setTagService(defaultTags.includes("service"));
  }, [defaultTags]);

  const pathPrefix = useMemo(() => {
    if (!hubId) return "";
    if (segment === "hub") return mediaPath({ entity: "hub", fileType: "media", hubId });
    if (!bizId) return "";
    if (segment === "biz") return mediaPath({ entity: "biz", fileType: "media", hubId, bizId });
    return mediaPath({ entity: "item", fileType: "media", hubId, bizId });
  }, [segment, hubId, bizId]);

  const gallerySlot = useSelector((state) =>
    pathPrefix ? state.gallery.manifestByPathPrefix[pathPrefix] : undefined
  );
  const manifest = gallerySlot?.manifest ?? EMPTY_MANIFEST;
  const imageCacheNonce = gallerySlot?.imageCacheNonce ?? 0;
  const manifestRef = useRef(manifest);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deletingKey, setDeletingKey] = useState(null);
  const [batchDeleting, setBatchDeleting] = useState(false);
  /** Selected manifest imageKeys (click tiles); used for batch delete and hub/biz display when exactly one. */
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [loadingManifest, setLoadingManifest] = useState(true);
  useEffect(() => {
    manifestRef.current = manifest;
  }, [manifest]);

  useEffect(() => {
    setSelectedKeys([]);
  }, [pathPrefix]);

  const reloadManifest = useCallback(
    async (reloadOptions = {}) => {
      const { preferOrigin = true } = reloadOptions;
      if (!pathPrefix) {
        setLoadingManifest(false);
        return;
      }
      setLoadingManifest(true);
      try {
        invalidateManifestFetchCacheForPath(pathPrefix);
        const remote = await fetchManifestForPath(pathPrefix, { token, preferOrigin });
        const prev =
          store.getState().gallery.manifestByPathPrefix[pathPrefix]?.manifest ?? {
            version: 1,
            images: [],
          };
        const chosen = preferNewerOrRicherManifest(prev, remote);
        dispatch(
          setGalleryManifestForPath({
            pathPrefix,
            manifest: chosen,
            bumpImageCache: false,
          })
        );
      } finally {
        setLoadingManifest(false);
      }
    },
    [pathPrefix, token, dispatch, store]
  );

  const refreshGalleryFromCdn = useCallback(() => {
    if (!pathPrefix) return;
    dispatch(clearGalleryManifestForPath(pathPrefix));
    void reloadManifest({ preferOrigin: true });
  }, [pathPrefix, dispatch, reloadManifest]);

  useEffect(() => {
    void reloadManifest({ preferOrigin: false });
  }, [reloadManifest]);

  const selectedManifestTags = useMemo(() => {
    if (segment !== "items") return [];
    const t = [];
    if (tagProduct) t.push("product");
    if (tagService) t.push("service");
    return t;
  }, [segment, tagProduct, tagService]);

  const manifestTagsForUpload = useMemo(() => {
    if (segment === "hub") return ["hub"];
    if (segment === "biz") return ["biz"];
    return selectedManifestTags.length > 0 ? selectedManifestTags : [];
  }, [segment, selectedManifestTags]);

  const handleUploadBatch = async (filesInput) => {
    const inputFiles = (Array.isArray(filesInput) ? filesInput : [filesInput]).filter(
      (file) => file instanceof File
    );
    const files = inputFiles.filter((file) => {
      if (isHeicFile(file)) {
        message.error(`${file.name}: HEIC/HEIF images are not supported. Use JPG, PNG, or WebP.`);
        return false;
      }
      if (!VALID_TYPES.includes(file.type)) {
        message.warning(`${file.name}: unsupported type (JPG/PNG/WebP only)`);
        return false;
      }
      return true;
    });
    if (files.length === 0) return;

    if (!token || !hubId) {
      message.warning("Missing hub.");
      return;
    }
    if (segment !== "hub" && !bizId) {
      message.warning("Select a Biz for this segment.");
      return;
    }
    if (segment === "items" && manifestTagsForUpload.length === 0) {
      message.warning("Select at least one tag (product or service).");
      return;
    }
    const currentCount = manifestRef.current?.images?.length || 0;
    if (Number.isFinite(imageLimit)) {
      if (currentCount >= imageLimit) {
        message.warning(`Your current plan allows at most ${imageLimit} images in this gallery.`);
        return;
      }
      if (currentCount + files.length > imageLimit) {
        message.warning(
          `You can add at most ${imageLimit - currentCount} more image(s) (selected ${files.length}).`
        );
        return;
      }
    }

    setUploading(true);
    setUploadProgress(0);
    const totalFiles = files.length;
    try {
      if (pathPrefix && token && (segment === "hub" || segment === "biz")) {
        const preManifest = manifestRef.current;
        const remoteManifest = await fetchManifestForPath(pathPrefix, { token });
        const remoteImages = Array.isArray(remoteManifest?.images)
          ? remoteManifest.images
          : [];
        const preImages = Array.isArray(preManifest?.images) ? preManifest.images : [];
        const rv = Number(remoteManifest?.version) || 0;
        const pv = Number(preManifest?.version) || 0;
        let mergedImages;
        if (remoteImages.length === 0) {
          mergedImages = preImages;
        } else if (preImages.length === 0) {
          mergedImages = remoteImages;
        } else {
          mergedImages = rv >= pv ? remoteImages : preImages;
        }
        const mergedManifest = {
          version: Math.max(rv, pv) || 1,
          images: mergedImages,
        };
        manifestRef.current = mergedManifest;
        dispatch(
          setGalleryManifestForPath({
            pathPrefix,
            manifest: mergedManifest,
            bumpImageCache: false,
          })
        );
      }

      const imagesMeta = files.map((file) => ({
        fileName: file.name,
        sizeBytes: file.size,
      }));
      const itemId =
        segment === "hub"
          ? "hub-gallery"
          : segment === "biz"
            ? "biz-gallery"
            : ITEMS_SHARED_GALLERY_ITEM_ID;
      const itemData = {
        hubId,
        itemId,
        images: imagesMeta,
        gallerySegment: segment,
        manifestTags: manifestTagsForUpload,
        operationType: operationTypeForGalleryUpload(segment),
      };
      if (segment !== "hub") {
        itemData.storeId = cdnSafeMediaPathSegment(bizId);
      }
      const res = await axios.post(
        handleMedia,
        Encrypt({
          token,
          action: "uploadImages",
          itemData,
        }),
        { headers: { "Content-Type": "application/json" } }
      );
      const result = res.data;
      if (!result.success) {
        message.error(result.message || "Upload failed.");
        return;
      }
      const imageToUpload = result.uploadedImages || [];
      if (imageToUpload.length !== files.length) {
        message.error("Unexpected upload response (presigned URL count mismatch).");
        return;
      }

      for (let index = 0; index < imageToUpload.length; index += 1) {
        const row = imageToUpload[index];
        const file = files[index];
        const { presignedUrl, contentType, imageKey } = row;
        if (!imageKey) {
          message.error("No image key returned.");
          return;
        }
        await putPresignedObject(presignedUrl, file, contentType || file.type);
        setUploadProgress(Math.round(((index + 1) / totalFiles) * 100));
      }

      const newEntries = imageToUpload.map((row, index) => {
        const file = files[index];
        const basename = basenameFromStorageKey(row.imageKey) || row.imageKey;
        return {
          imageKey: basename,
          originalFilename: row.originalName || file.name,
          tags: [...manifestTagsForUpload],
        };
      });

      const prevM = manifestRef.current;
      let mergedImages = prevM.images || [];
      for (const entry of newEntries) {
        mergedImages =
          segment === "hub" || segment === "biz"
            ? (
                Number.isFinite(imageLimit)
                  ? [...mergedImages.filter((e) => e.imageKey !== entry.imageKey), entry]
                      .slice(0, imageLimit)
                  : [...mergedImages.filter((e) => e.imageKey !== entry.imageKey), entry]
              )
            : [...mergedImages.filter((e) => e.imageKey !== entry.imageKey), entry];
      }
      const nextManifest = {
        ...prevM,
        version: (prevM.version || 1) + 1,
        images: mergedImages,
      };
      if (result.manifestPresignedUrl) {
        try {
          await putManifestWithPresignedUrl(
            result.manifestPresignedUrl,
            nextManifest,
            putManifestCrypto,
            manifestPutOptions
          );
        } catch (manifestErr) {
          console.error(manifestErr);
          message.warning(
            "Image(s) uploaded, but saving manifest.json failed. The gallery list may not update on other devices."
          );
        }
      } else {
        message.warning(
          "Image(s) uploaded, but no manifest upload URL was returned. Gallery manifest may be out of sync."
        );
      }
      manifestRef.current = nextManifest;
      dispatch(
        setGalleryManifestForPath({
          pathPrefix,
          manifest: nextManifest,
          bumpImageCache: true,
        })
      );
      message.success(
        result.message ||
          `${newEntries.length} image(s) uploaded${result.cdnEnabled ? " (CDN)" : ""}.`
      );
      if (segment === "items" && typeof onItemsGalleryUploadComplete === "function") {
        /** One callback with all entries so parents can call `onChange` once (avoids stale Form state). */
        onItemsGalleryUploadComplete(newEntries);
      }
    } catch (e) {
      console.error(e);
      message.error(e?.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const thumbUrl = (entry) => {
    if (!pathPrefix || !entry?.imageKey) return null;
    const base = cdnUrlForStorageKey(`${pathPrefix}/${entry.imageKey}`);
    if (!base) return null;
    const v = Number(manifest?.version) || 0;
    const sep = base.includes("?") ? "&" : "?";
    return `${base}${sep}v=${encodeURIComponent(String(v))}&n=${encodeURIComponent(
      String(imageCacheNonce)
    )}`;
  };

  /** @returns {Promise<boolean>} */
  const setBizDisplayImage = async (fullKeyOrBasename) => {
    if (!hubId || !bizId || !fullKeyOrBasename) return false;
    if (bizVersion === undefined || bizVersion === null) {
      message.error("Missing Biz version; refresh the Biz list and try again.");
      return false;
    }
    const keyForApi = basenameFromStorageKey(fullKeyOrBasename);
    try {
      await updateBizDetailsPatch({
        token,
        biz: {
          bizId,
          hubId,
          version: bizVersion,
          bizImageKey: keyForApi,
          updateTarget: "METADATA",
          bizOnlyPatch: true,
        },
      });
      message.success("Biz display image saved.");
      onBizBrandingSaved?.();
      return true;
    } catch (e) {
      console.error(e);
      message.error("Could not save Biz display image.");
      return false;
    }
  };

  /** @returns {Promise<boolean>} */
  const setHubDisplayImage = async (fullKeyOrBasename) => {
    if (!hubId || !fullKeyOrBasename) return false;
    const key = basenameFromStorageKey(fullKeyOrBasename);
    try {
      await updateHubDetailsPatch({ token, hubId, hubImageKey: key });
      dispatch(setHubImageKey(key));
      message.success("Hub display image saved.");
      onHubImageSaved?.();
      return true;
    } catch (e) {
      console.error(e);
      message.error("Could not save Hub display image.");
      return false;
    }
  };

  const isBizDisplayBasename = (basename) =>
    bizImageKeyFromDb &&
    basenameFromStorageKey(bizImageKeyFromDb) === basenameFromStorageKey(basename);

  const isHubDisplayBasename = (basename) =>
    hubImageKeyFromDb &&
    basenameFromStorageKey(hubImageKeyFromDb) === basenameFromStorageKey(basename);

  const maybeClearDisplayIfDeleted = async (deletedBasename) => {
    if (segment === "hub" && isHubDisplayBasename(deletedBasename)) {
      try {
        await updateHubDetailsPatch({ token, hubId, hubImageKey: "" });
        dispatch(setHubImageKey(""));
        onHubImageSaved?.();
      } catch (e) {
        console.error(e);
      }
    }
    if (
      segment === "biz" &&
      isBizDisplayBasename(deletedBasename) &&
      bizVersion !== undefined &&
      bizVersion !== null &&
      hubId &&
      bizId
    ) {
      try {
        await updateBizDetailsPatch({
          token,
          biz: {
            bizId,
            hubId,
            version: bizVersion,
            bizImageKey: "",
            updateTarget: "METADATA",
            bizOnlyPatch: true,
          },
        });
        onBizBrandingSaved?.();
      } catch (e) {
        console.error(e);
      }
    }
  };

  const toggleSelectKey = (k) => {
    if (!k || batchDeleting || deletingKey != null) return;
    setSelectedKeys((prev) => {
      const i = prev.indexOf(k);
      if (i >= 0) return prev.filter((x) => x !== k);
      return [...prev, k];
    });
  };

  /** After user already confirmed removal: optional DB unlink (items segment), then S3 + manifest. */
  const executeDeleteAfterConsent = async (entries) => {
    if (segment !== "items" || !hubId || !bizId || !token) {
      await deleteGalleryEntries(entries);
      return;
    }
    const keysToRemove = [...new Set(entries.map((e) => e?.imageKey).filter(Boolean))];
    if (keysToRemove.length === 0) return;
    try {
      const findRes = await findItemsUsingGalleryImageBasenames({
        token,
        hubId,
        bizId,
        imageBasenames: keysToRemove,
      });
      const links = Array.isArray(findRes?.links) ? findRes.links : [];

      const unlinkIfNeededThenDelete = async () => {
        if (links.length > 0) {
          await unlinkGalleryImagesFromItems({
            token,
            hubId,
            bizId,
            imageBasenames: keysToRemove,
          });
        }
        await deleteGalleryEntries(entries);
      };

      if (links.length === 0) {
        await deleteGalleryEntries(entries);
        return;
      }

      const names = links.map((l) => l.itemName || l.itemId).filter(Boolean);
      const nameLine =
        names.length <= 5
          ? names.join(", ")
          : `${names.slice(0, 5).join(", ")} and ${names.length - 5} more`;

      await new Promise((resolve, reject) => {
        modal.confirm({
          title: "Used by catalog items",
          content: `This media is linked to ${links.length} item(s): ${nameLine}. It will be removed from those items, then deleted from storage.`,
          okText: "Remove everywhere",
          okButtonProps: { danger: true },
          onOk: async () => {
            try {
              await unlinkIfNeededThenDelete();
              resolve();
            } catch (err) {
              reject(err);
            }
          },
          onCancel: () => resolve(),
        });
      });
    } catch (e) {
      console.error(e);
      message.error(e?.message || "Could not check item references.");
    }
  };

  const deleteGalleryEntries = async (entries) => {
    if (!token || !pathPrefix || !entries?.length) return;
    const keysToRemove = [...new Set(entries.map((e) => e?.imageKey).filter(Boolean))];
    if (keysToRemove.length === 0) return;
    const fullKeys = keysToRemove
      .map((k) => `${pathPrefix.replace(/\/$/, "")}/${String(k).replace(/^\//, "")}`)
      .filter(Boolean);
    if (fullKeys.length !== keysToRemove.length) return;

    const isMulti = keysToRemove.length > 1;
    if (isMulti) setBatchDeleting(true);
    else setDeletingKey(keysToRemove[0]);

    try {
      const res = await axios.post(
        handleMedia,
        Encrypt({
          token,
          action: "deleteImages",
          itemData: { imageKeys: fullKeys },
        }),
        { headers: { "Content-Type": "application/json" } }
      );
      const result = res.data;
      if (!result?.success) {
        message.error(result?.message || "Could not remove image(s).");
        return;
      }
      const keySet = new Set(keysToRemove);
      const prevM = manifestRef.current;
      const prevImages = Array.isArray(prevM.images) ? prevM.images : [];
      const nextImages = prevImages.filter((e) => !keySet.has(e.imageKey));
      const nextManifest = {
        ...prevM,
        version: (prevM.version || 1) + 1,
        images: nextImages,
      };
      const putUrl = await requestManifestPutPresignedUrl(token, pathPrefix);
      if (putUrl) {
        try {
          await putManifestWithPresignedUrl(putUrl, nextManifest, putManifestCrypto, manifestPutOptions);
        } catch (manifestErr) {
          console.error(manifestErr);
          message.warning(
            "Removed from storage, but saving the updated gallery list failed. Use Refresh media to reload."
          );
        }
      } else {
        message.warning(
          "Removed from storage, but no upload URL was returned for the gallery list. Use Refresh media to reload."
        );
      }
      manifestRef.current = nextManifest;
      dispatch(
        setGalleryManifestForPath({
          pathPrefix,
          manifest: nextManifest,
          bumpImageCache: true,
        })
      );
      for (const k of keysToRemove) {
        await maybeClearDisplayIfDeleted(k);
      }
      message.success(
        keysToRemove.length > 1 ? `${keysToRemove.length} items removed.` : "Image removed."
      );
      setSelectedKeys([]);
      void reloadManifest({ preferOrigin: true });
    } catch (e) {
      console.error(e);
      message.error(e?.message || "Could not remove image(s).");
    } finally {
      if (isMulti) setBatchDeleting(false);
      else setDeletingKey(null);
    }
  };

  const confirmDeleteImage = (entry) => {
    modal.confirm({
      title: "Remove this image?",
      content: "It will be deleted from storage and removed from this gallery.",
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: () => executeDeleteAfterConsent([entry]),
    });
  };

  const images = manifest.images || [];
  const hubBizGallery = segment === "hub" || segment === "biz";
  const displayImages = Number.isFinite(imageLimit)
    ? images.slice(0, imageLimit)
    : images;
  const galleryAtCapacity =
    Number.isFinite(imageLimit) && images.length >= imageLimit;

  const confirmDeleteSelected = () => {
    const keys = new Set(selectedKeys);
    const toDelete = displayImages.filter((e) => keys.has(e.imageKey));
    if (toDelete.length === 0) return;
    const n = toDelete.length;
    modal.confirm({
      title: n > 1 ? `Remove ${n} media files?` : "Remove this file?",
      content: "They will be deleted from storage and removed from this gallery list.",
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: () => executeDeleteAfterConsent(toDelete),
    });
  };

  const handleToolbarSetHubDisplay = async () => {
    if (selectedKeys.length !== 1) return;
    const ok = await setHubDisplayImage(selectedKeys[0]);
    if (ok) setSelectedKeys([]);
  };

  const handleToolbarSetBizDisplay = async () => {
    if (selectedKeys.length !== 1) return;
    const ok = await setBizDisplayImage(selectedKeys[0]);
    if (ok) setSelectedKeys([]);
  };

  const actionBusy =
    uploading || loadingManifest || batchDeleting || deletingKey != null;

  const allVisibleSelected =
    displayImages.length > 0 &&
    displayImages.every((e) => selectedKeys.includes(e.imageKey));

  const handleSelectAllVisible = () => {
    setSelectedKeys(displayImages.map((e) => e.imageKey).filter(Boolean));
  };

  const handleClearSelection = () => setSelectedKeys([]);

  /** Tags under tile: hide segment-only manifest tags on hub/biz; keep product/service on items. */
  const visibleTagsForEntry = (entry) => {
    const raw = entry?.tags;
    if (!Array.isArray(raw) || raw.length === 0) return [];
    if (segment === "hub") {
      return raw.filter((t) => String(t).toLowerCase() !== "hub");
    }
    if (segment === "biz") {
      return raw.filter((t) => String(t).toLowerCase() !== "biz");
    }
    return raw;
  };

  if (!pathPrefix) {
    return <Text type="secondary">Select hub{segment !== "hub" ? " and Biz" : ""} to load this gallery.</Text>;
  }

  return (
    <div>
      {segment === "items" && (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
            Click thumbnails to select, then delete selected from the toolbar.
          </Text>
          <Text strong style={{ marginRight: 8 }}>
            Tags for this upload
          </Text>
          <Tooltip title="Shared pool for product and service photos on your storefront. Pick tags so images sort correctly.">
            <InfoCircleOutlined style={{ marginRight: 12, color: "var(--zh-icon-muted)" }} />
          </Tooltip>
          <Checkbox checked={tagProduct} onChange={(e) => setTagProduct(e.target.checked)}>
            Product
          </Checkbox>
          <Checkbox checked={tagService} onChange={(e) => setTagService(e.target.checked)}>
            Service
          </Checkbox>
        </div>
      )}
      {hubBizGallery && Number.isFinite(imageLimit) && (
        <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
          Up to {imageLimit} images. Click thumbnails to select; with one selected, use the toolbar to set
          the public display image or delete. Hover for quick delete.
        </Text>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <Space wrap size="middle">
          <Upload
            multiple
            beforeUpload={(file, fileList) => {
              // rc-upload passes native `File[]` here (not Ant UploadFile); do not use .originFileObj.
              const files = Array.isArray(fileList)
                ? fileList.filter((candidate) => candidate instanceof File)
                : [];
              const isLast =
                files.length > 0 && files[files.length - 1] === file;
              if (isLast && files.length > 0) {
                void handleUploadBatch(files);
              }
              return false;
            }}
            showUploadList={false}
            accept={IMAGE_UPLOAD_ACCEPT}
            disabled={
              uploading ||
              loadingManifest ||
              galleryAtCapacity ||
              batchDeleting ||
              deletingKey != null ||
              privateManifestLocked
            }
          >
            <Button
              icon={<UploadOutlined />}
              loading={uploading && !galleryAtCapacity}
              disabled={
                galleryAtCapacity ||
                batchDeleting ||
                deletingKey != null ||
                privateManifestLocked
              }
              type="primary"
              size={compact ? "middle" : "large"}
            >
              {galleryAtCapacity
                ? "Gallery full"
                : hubBizGallery && images.length > 0
                  ? "Add image"
                  : "Upload image"}
            </Button>
          </Upload>
          {displayImages.length > 0 && (
            <>
              <Button
                size={compact ? "middle" : "large"}
                disabled={actionBusy || allVisibleSelected}
                onClick={handleSelectAllVisible}
              >
                Select all
              </Button>
              <Button
                size={compact ? "middle" : "large"}
                disabled={actionBusy || selectedKeys.length === 0}
                onClick={handleClearSelection}
              >
                Clear selection
              </Button>
            </>
          )}
          {segment === "hub" && (
            <Button
              type="primary"
              size={compact ? "middle" : "large"}
              disabled={
                selectedKeys.length !== 1 ||
                batchDeleting ||
                deletingKey != null ||
                uploading ||
                loadingManifest
              }
              onClick={handleToolbarSetHubDisplay}
            >
              Set as Hub display
            </Button>
          )}
          {segment === "biz" && (
            <Button
              type="primary"
              size={compact ? "middle" : "large"}
              disabled={
                selectedKeys.length !== 1 ||
                batchDeleting ||
                deletingKey != null ||
                uploading ||
                loadingManifest
              }
              onClick={handleToolbarSetBizDisplay}
            >
              Set as Biz display
            </Button>
          )}
          <Button
            danger
            icon={<DeleteOutlined />}
            size={compact ? "middle" : "large"}
            disabled={
              selectedKeys.length === 0 ||
              batchDeleting ||
              deletingKey != null ||
              uploading ||
              loadingManifest ||
              privateManifestLocked
            }
            loading={batchDeleting}
            onClick={confirmDeleteSelected}
          >
            Delete selected ({selectedKeys.length})
          </Button>
        </Space>
        <Button
          type="text"
          icon={<ReloadOutlined />}
          size={compact ? "middle" : "large"}
          disabled={actionBusy}
          onClick={refreshGalleryFromCdn}
          aria-label="Refresh media"
          title="Refresh media"
        />
      </div>

      {uploading && (
        <div style={{ marginTop: 16, padding: '0 4px' }}>
          <Progress
            percent={uploadProgress}
            showInfo={false}
            strokeColor="var(--zh-brand-primary)"
            trailColor="var(--zh-border)"
            size="small"
          />
          <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
            Uploading {uploadProgress < 100 ? `${uploadProgress}%` : 'complete…'}
          </Text>
        </div>
      )}
      <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
        {loadingManifest && (
          <Col span={24}>
            <div className="gallery-empty-state">
              <div className="gallery-empty-icon">{/* Loading spinner via Ant Spin or inline */}</div>
              <p className="gallery-empty-text">Loading media…</p>
            </div>
          </Col>
        )}
        {!loadingManifest && displayImages.length === 0 && (
          <Col span={24}>
            <div className="gallery-empty-state">
              <div className="gallery-empty-icon">
                <UploadOutlined style={{ fontSize: 36 }} />
              </div>
              <h3 className="gallery-empty-title">No photos yet</h3>
              <p className="gallery-empty-text">Upload an image to get started.</p>
            </div>
          </Col>
        )}
        {!loadingManifest &&
          displayImages.map((entry) => {
            const src = thumbUrl(entry);
            const k = entry.imageKey;
            const imgKey = `${k}-m${Number(manifest?.version) || 0}-n${imageCacheNonce}`;
            const thumbH = compact ? 96 : 120;
            const selected = selectedKeys.includes(k);
            const tagLine = visibleTagsForEntry(entry);
            const isDisplayImage =
              (segment === "hub" && isHubDisplayBasename(k)) ||
              (segment === "biz" && isBizDisplayBasename(k));
            return (
              <Col xs={12} sm={8} md={6} key={imgKey}>
                <Card size="small" bordered={false} styles={{ body: { padding: 0 } }}>
                  <div
                    className="gallery-thumb-wrap"
                    style={{
                      position: "relative",
                      borderRadius: 8,
                      marginBottom: tagLine.length > 0 ? 6 : 0,
                      border: selected ? "2px solid var(--zh-brand-primary)" : "1px solid var(--zh-border-md)",
                      overflow: "hidden",
                      boxSizing: "border-box",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => toggleSelectKey(k)}
                      disabled={
                        batchDeleting ||
                        deletingKey != null ||
                        uploading ||
                        loadingManifest ||
                        privateManifestLocked
                      }
                      style={{
                        display: "block",
                        width: "100%",
                        padding: 0,
                        border: "none",
                        borderRadius: 0,
                        overflow: "visible",
                        background: "var(--zh-muted)",
                        cursor:
                          batchDeleting ||
                          deletingKey != null ||
                          uploading ||
                          loadingManifest ||
                          privateManifestLocked
                            ? "not-allowed"
                            : "pointer",
                        opacity:
                          batchDeleting ||
                          deletingKey != null ||
                          uploading ||
                          loadingManifest ||
                          privateManifestLocked
                            ? 0.7
                            : 1,
                        lineHeight: 0,
                        position: "relative",
                      }}
                    >
                      {src ? (
                        <img
                          alt=""
                          key={imgKey}
                          src={src}
                          style={{ display: "block", width: "100%", height: thumbH, objectFit: "cover" }}
                        />
                      ) : (
                        <div style={{ height: thumbH, background: "var(--zh-muted)" }} />
                      )}
                      {isDisplayImage && (
                        <Tooltip
                          title={
                            segment === "hub"
                              ? "This image is used as your Hub storefront display."
                              : "This image is used as your Biz storefront display."
                          }
                        >
                          <span className="gallery-display-badge">
                            <StarFilled style={{ color: "var(--zh-warning)", fontSize: 12 }} />
                          </span>
                        </Tooltip>
                      )}
                    </button>
                    <div className="gallery-thumb-actions">
                      <span className="gallery-thumb-delete-hit">
                        <Button
                          size="small"
                          danger
                          type="primary"
                          icon={<DeleteOutlined />}
                          loading={deletingKey === k}
                          disabled={
                            privateManifestLocked ||
                            batchDeleting ||
                            (deletingKey != null && deletingKey !== k)
                          }
                          className="gallery-thumb-delete-btn"
                          aria-label="Delete from gallery"
                          title="Delete from gallery"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            confirmDeleteImage(entry);
                          }}
                        />
                      </span>
                    </div>
                  </div>
                  {tagLine.length > 0 ? (
                    <div style={{ padding: "0 2px 4px", minHeight: 0 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {tagLine.join(", ")}
                      </Text>
                    </div>
                  ) : null}
                </Card>
              </Col>
            );
          })}
      </Row>
    </div>
  );
};

export default GallerySegmentPanel;
